import { combineReducers } from 'redux'
import * as isNumber from 'lodash/isNumber'
import { Reducer } from 'interfaces'
import { Car, CarMake, CarModels, CarsDataState, Home, HomePrices, HomesDataState, PropertyDataState } from 'interfaces/property'
import {
  DELETE_CAR,
  DELETE_HOME,
  RESET_PROPERTY,
  SET_CAR_MAKES,
  SET_CAR_MODELS,
  SET_CARS,
  SET_HOME_PRICE,
  SET_HOMES,
  UNLINK_CAR,
  UNLINK_HOME,
} from 'actions/propertyActions'
import { createEmptyCollection, removeByKeyFromCollection, setItemInCollection } from 'helpers/normalizeHelpers'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_CARS_EDIT, FORM_HOMES_EDIT } from 'constants/forms'
import { FormNameType } from 'interfaces/form'


// ===========================================================================//
//                                     CARS                                   //
// ===========================================================================//

const carsReducer: Reducer<CarsDataState> = (state = null, action) => {
  switch (action.type) {
    case SET_CARS:
      return action.payload

    case RESET_PROPERTY:
      return null

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (FORM_CARS_EDIT !== action.payload.formName as FormNameType) {
        return state
      }

      const nextCar = action.payload.response as Car
      return setItemInCollection(state || createEmptyCollection(), nextCar, 'id')
    }

    case DELETE_CAR:
      return state
        ? removeByKeyFromCollection(state, action.payload)
        : state

    case UNLINK_CAR: {
      if (!state) {
        return state
      }

      const { id, date } = action.payload
      const nextCar = {
        ...state.byKey[id],
        unlinked_at: date,
      }

      return setItemInCollection(state, nextCar, 'id')
    }

    default:
      return state
  }
}

const carMakesReducer: Reducer<CarMake[]> = (state = [], action) => {
  switch (action.type) {
    case SET_CAR_MAKES:
      return action.payload

    default:
      return state
  }
}

const carModelsReducer: Reducer<CarModels> = (state = {}, action) => {
  switch (action.type) {
    case SET_CAR_MODELS:
      const payload = action.payload
      return Object.assign({}, state, { [payload.make]: payload.models })

    default:
      return state
  }
}

// ===========================================================================//
//                                    HOMES                                   //
// ===========================================================================//

const homesReducer: Reducer<HomesDataState> = (state = null, action) => {
  switch (action.type) {
    case SET_HOMES:
      return action.payload

    case RESET_PROPERTY:
      return null

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (!state || FORM_HOMES_EDIT !== action.payload.formName as FormNameType) {
        return state
      }

      const nextHome = action.payload.response as Home
      const prevPrimaryResidenceId = state.allKeys.find((id) => Boolean(state.byKey[id].is_primary_residence))

      // if there was another primary residence and nextHome is primary residence then alter old one
      const shouldUpdatePrimaryResidence = nextHome.is_primary_residence
        && nextHome.id !== prevPrimaryResidenceId
      const prevHomes = isNumber(prevPrimaryResidenceId) && shouldUpdatePrimaryResidence ?
        (() => {
          const prevPrimaryResidence = state.byKey[prevPrimaryResidenceId]
          const exPrimaryResidence = {
            ...prevPrimaryResidence,
            is_primary_residence: false,
          }

          return setItemInCollection(state, exPrimaryResidence, 'id')
        })() :
        state

      return setItemInCollection(prevHomes, nextHome, 'id')
    }

    case DELETE_HOME:
      return state
        ? removeByKeyFromCollection(state, action.payload)
        : null

    case UNLINK_HOME: {
      if (!state) {
        return state
      }

      const { id, date } = action.payload
      const nextHome = {
        ...state.byKey[id],
        unlinked_at: date,
      }

      return setItemInCollection(state, nextHome, 'id')
    }

    default:
      return state
  }
}

const homePricesReducer: Reducer<HomePrices> = (state = {}, action) => {
  switch (action.type) {
    case SET_HOME_PRICE:
      const { price, key } = action.payload
      return Object.assign({}, state, { [key]: price })

    default:
      return state
  }
}

export default combineReducers<PropertyDataState>({
  homes: homesReducer,
  homePrices: homePricesReducer,
  cars: carsReducer,
  carMakes: carMakesReducer,
  carModels: carModelsReducer,
})
